import React, { useEffect, useMemo } from 'react';
import { Stack, Text, Grid, Box, Alert, MultiSelect } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { t } from 'i18next';
import { TimeIntervalSelector } from './TimeIntervalSelector';
import { 
  saveSelectedPkBiomarkers, 
  getSelectedPkBiomarkers,
  saveSelectedExternalBiomarkers, 
  getSelectedExternalBiomarkers 
} from '../utils/localStorage';
import { groupBiomarkersByType } from '../utils';
import { useGetBiomarkers } from '../../../../global/entities/Biomarker/queries/useGetBiomarkers';
import { useFeatureVisibility } from '../../../../global/provider/featureVisibilityContext';

interface LabMetricsSelectorProps {
  selectedPkMetrics: string[];
  onPkMetricsChange: (values: string[]) => void;
  selectedExternalMetrics: string[];
  onExternalMetricsChange: (values: string[]) => void;
  startTime: string;
  endTime: string;
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
}

export const LabMetricsSelector: React.FC<LabMetricsSelectorProps> = ({
  selectedPkMetrics,
  onPkMetricsChange,
  selectedExternalMetrics,
  onExternalMetricsChange,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
}) => {
  // Fetch biomarkers from database
  const { data: biomarkers, isLoading, error } = useGetBiomarkers();

  // Gate each biomarker category by the facility's enabled modules (AMI-618):
  // sequenced (PK) biomarkers belong to Therapeutic Drug Monitoring, external
  // biomarkers belong to Lab Results.
  const { isFeatureVisible } = useFeatureVisibility();
  const labResultsVisible = isFeatureVisible('labResults');
  const tdmVisible = isFeatureVisible('taskType.tdm');

  // Transform and group biomarkers by type - excluding external biomarkers
  const { pkMetricsOptions, nonPkMetricsOptions } = useMemo(() => {
    if (!tdmVisible || !biomarkers || biomarkers.length === 0) return { pkMetricsOptions: [], nonPkMetricsOptions: [] };

    // Filter out external biomarkers for the main metrics
    const nonExternalBiomarkers = biomarkers.filter(biomarker => !biomarker.isExternal);
    const { pkBiomarkers, nonPkBiomarkers } = groupBiomarkersByType(nonExternalBiomarkers);
    
    // Transform to options format with 'lab-' prefix
    const pkOptions = pkBiomarkers
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((biomarker) => ({
        value: `lab-${biomarker.id}`,
        label: biomarker.name,
      }));
      
    const nonPkOptions = nonPkBiomarkers
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((biomarker) => ({
        value: `lab-${biomarker.id}`,
        label: biomarker.name,
      }));
    
    return { pkMetricsOptions: pkOptions, nonPkMetricsOptions: nonPkOptions };
  }, [biomarkers, tdmVisible]);

  // External biomarkers for multi-select with 'lab-' prefix for consistency
  const externalMetricsOptions = useMemo(() => {
    if (!labResultsVisible || !biomarkers || biomarkers.length === 0) return [];

    return biomarkers
      .filter(biomarker => biomarker.isExternal)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((biomarker) => ({
        value: `lab-${biomarker.id}`,
        label: biomarker.name,
      }));
  }, [biomarkers, labResultsVisible]);

  // Show time interval if any PK biomarker is selected
  const showTimeInterval = useMemo(() => {
    return selectedPkMetrics.length > 0;
  }, [selectedPkMetrics]);

  // Load saved PK biomarkers
  useEffect(() => {
    const savedPkMetrics = getSelectedPkBiomarkers();
    if (savedPkMetrics.length > 0 && pkMetricsOptions.length > 0) {
      // Filter out any saved metrics that no longer exist
      const validSavedMetrics = savedPkMetrics.filter(metricId => 
        pkMetricsOptions.some(option => option.value === metricId)
      );
      if (validSavedMetrics.length > 0) 
        onPkMetricsChange(validSavedMetrics);
      
    }
  }, [pkMetricsOptions, onPkMetricsChange]);

  // Load saved external biomarkers
  useEffect(() => {
    const savedExternalMetrics = getSelectedExternalBiomarkers();
    if (savedExternalMetrics.length > 0 && externalMetricsOptions.length > 0) {
      // Filter out any saved metrics that no longer exist
      const validSavedMetrics = savedExternalMetrics.filter(metricId => 
        externalMetricsOptions.some(option => option.value === metricId)
      );
      if (validSavedMetrics.length > 0) 
        onExternalMetricsChange(validSavedMetrics);
      
    }
  }, [externalMetricsOptions, onExternalMetricsChange]);

  const handlePkMetricsChange = (values: string[]) => {
    onPkMetricsChange(values);
    saveSelectedPkBiomarkers(values);
  };

  const handleExternalMetricsChange = (values: string[]) => {
    onExternalMetricsChange(values);
    saveSelectedExternalBiomarkers(values);
  };

  // Loading state (only relevant to the sequenced/PK selector, which is TDM-gated)
  if (isLoading && tdmVisible) {
    return (
      <Stack gap="xs">
        <Grid align="top">
          <Grid.Col span={4}>
            <Text fw={600}>{t('graph.selectPkBiomarkers')}</Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <Text size="sm" c="dimmed">{t('common.loading')}...</Text>
          </Grid.Col>
        </Grid>
      </Stack>
    );
  }

  // Error state (only relevant to the sequenced/PK selector, which is TDM-gated)
  if (error && tdmVisible) {
    return (
      <Stack gap="xs">
        <Grid align="top">
          <Grid.Col span={4}>
            <Text fw={600}>{t('graph.selectPkBiomarkers')}</Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <Alert icon={<IconInfoCircle size="1rem" />} color="red" variant="light">
              {t('common.errorLoadingData')}
            </Alert>
          </Grid.Col>
        </Grid>
      </Stack>
    );
  }

  // No data state
  if (pkMetricsOptions.length === 0 && nonPkMetricsOptions.length === 0 && externalMetricsOptions.length === 0) return null;

  return (
    <Stack gap="xs">
      {externalMetricsOptions.length > 0 && (
        <Grid align="center">
          <Grid.Col span={4}>
            <Text fw={600}>{t('graph.selectBiomarkers')}</Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <Box style={{ flex: 'initial' }}>
              <div style={{ position: 'relative', width: '100%', maxWidth: '250px' }} className="hide-pills">
                <style>
                  {`
                  .hide-pills .mantine-Pill-root {
                    display: none !important;
                  }
                  `}
                </style>
                <MultiSelect
                  data={externalMetricsOptions}
                  value={selectedExternalMetrics}
                  onChange={handleExternalMetricsChange}
                  placeholder={t('graph.biomarkersSelected', { count: selectedExternalMetrics.length })}
                  searchable
                  clearable
                  w="100%"
                />
              </div>
            </Box>
          </Grid.Col>
        </Grid>
      )}
      
      {pkMetricsOptions.length > 0 && (
        <Grid align="center">
          <Grid.Col span={4}>
            <Text fw={600}>{t('graph.selectPkBiomarkers')}</Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <Box style={{ flex: 'initial' }}>
              <div style={{ position: 'relative', width: '100%', maxWidth: '250px' }} className="hide-pills">
                <style>
                  {`
                  .hide-pills .mantine-Pill-root {
                    display: none !important;
                  }
                  `}
                </style>
                <MultiSelect
                  data={pkMetricsOptions}
                  value={selectedPkMetrics}
                  onChange={handlePkMetricsChange}
                  placeholder={t('graph.pkBiomarkersSelected', { count: selectedPkMetrics.length })}
                  searchable
                  clearable
                  w="100%"
                />
              </div>
            </Box>
          </Grid.Col>
        </Grid>
      )}

      {showTimeInterval && (
        <TimeIntervalSelector
          startTime={startTime}
          endTime={endTime}
          onStartTimeChange={onStartTimeChange}
          onEndTimeChange={onEndTimeChange}
        />
      )}
    </Stack>
  );
}; 